@if (showSummarySection()) {
  @if (!hasPlannings()) {
    <div class="rounded-2xl border border-dashed border-border">
      <app-state-panel
        state="empty"
        title="Noch keine Planung berechnet"
        description="Aufgaben 4 und 5 erzeugen die Modulbelegung und Wirtschaftlichkeitsberechnung."
      />
    </div>
  } @else {
    @if (selectedPlanning(); as planning) {
      <section>
      <div class="flex flex-wrap items-center gap-2 pb-4">
        <button
          type="button"
          class="group inline-flex items-center gap-1.5 border-b border-foreground/25 pb-0.5 text-xs font-normal text-foreground transition-colors hover:border-foreground"
          [brnMenuTriggerFor]="planningMenu"
        >
          <span>{{ getPlanningLabel(planning, sortedPlannings().indexOf(planning)) }}</span>
          <span class="font-mono text-2xs text-muted-foreground">{{ formatDate(planning.updatedAt) }}</span>
          <ng-icon name="lucideChevronDown" size="13" class="text-muted-foreground transition-transform duration-150" />
        </button>
        @if (mode() === "admin" && planning.status === "completed") {
          @if (planning.id === primaryPlanningId()) {
            <span class="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-normal text-muted-foreground">
              <ng-icon name="lucideStar" size="13" class="*:*:fill-current" />
              Hauptplanung
            </span>
          } @else {
            <button
              type="button"
              class="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-normal text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              (click)="onSetPrimaryPlanning(planning.id)"
            >
              <ng-icon name="lucideStar" size="13" />
              Als Hauptplanung festlegen
            </button>
          }

          <span class="h-4 w-px bg-border" aria-hidden="true"></span>
          <button
            type="button"
            class="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-normal text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-60"
            [class.text-destructive]="reportExport()?.status === 'failed'"
            [disabled]="reportExportBusy() || reportExport()?.status === 'queued' || reportExport()?.status === 'running'"
            [title]="reportExport()?.errorMessage || reportExportLabel()"
            (click)="onReportExportAction(planning.id)"
          >
            <ng-icon
              [name]="reportExportBusy() || reportExport()?.status === 'queued' || reportExport()?.status === 'running' ? 'lucideLoader' : 'lucideFileDown'"
              size="13"
              [class.animate-spin]="reportExportBusy() || reportExport()?.status === 'queued' || reportExport()?.status === 'running'"
            />
            {{ reportExportLabel() }}
          </button>
          @if (reportExport()?.status === 'completed') {
            <button
              type="button"
              class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title="PDF neu erstellen"
              aria-label="PDF neu erstellen"
              (click)="createReportExport(planning.id)"
            >
              <ng-icon name="lucideRefreshCw" size="13" />
            </button>
          }
        }
        @if (runningPlanningCount() > 0) {
          <span hlmBadge variant="outline" class="border-primary/20 bg-accent text-2xs text-accent-foreground">
            {{ runningPlanningCount() }} aktiv
          </span>
        }

        <ng-template #planningMenu>
          <hlm-menu class="w-72">
            <hlm-menu-group>
              <div class="max-h-64 overflow-y-auto">
                @for (item of sortedPlannings(); track item.id) {
                  <button
                    hlmMenuItem
                    class="flex items-center gap-2"
                    [class.bg-accent]="isPlanningSelected(item, sortedPlannings().indexOf(item))"
                    (click)="selectPlanning(sortedPlannings().indexOf(item))"
                  >
                    <ng-icon
                      [name]="getPlanningStatusIcon(item)"
                      size="13"
                      class="shrink-0"
                      [class.animate-spin]="item.status === 'running'"
                    />
                    @if (item.id === primaryPlanningId()) {
                      <ng-icon name="lucideStar" size="12" class="shrink-0 text-primary *:*:fill-current" />
                    }
                    <span class="min-w-0 flex-1 truncate">{{ getPlanningLabel(item, sortedPlannings().indexOf(item)) }}</span>
                    <span hlmBadge variant="outline" class="shrink-0 text-2xs" [class]="getPlanningStatusClass(item)">
                      {{ getPlanningStatusLabel(item) }}
                    </span>
                  </button>
                }
              </div>
              @if (canCreatePlanning()) {
                <hlm-menu-separator />
                <button hlmMenuItem class="gap-2 font-normal" (click)="onCreatePlanning()">
                  <ng-icon name="lucidePlus" size="14" />
                  Neue Planung erstellen
                </button>
              }
            </hlm-menu-group>
          </hlm-menu>
        </ng-template>

        <div class="ml-auto flex items-center gap-2">
          <span hlmBadge variant="outline" class="h-6 gap-1 rounded-md font-mono text-xs" [class]="getPlanningStatusClass(planning)">
            <ng-icon [name]="getPlanningStatusIcon(planning)" size="11" [class.animate-spin]="planning.status === 'running'" />
            {{ getPlanningStatusLabel(planning) }}
          </span>
          @if (planning.status === 'completed') {
            <span
              hlmBadge
              variant="outline"
              [class]="planning.worthwhile
                ? 'h-6 gap-1 rounded-md border-transparent bg-success-soft font-mono text-xs text-success'
                : 'h-6 gap-1 rounded-md border-transparent bg-danger-soft font-mono text-xs text-danger'"
            >
              <ng-icon [name]="planning.worthwhile ? 'lucideCheck' : 'lucideX'" size="11" />
              {{ planning.worthwhile ? "Wirtschaftlich" : "Nicht wirtschaftlich" }}
            </span>
          }
          @if (mode() === "admin") {
            <button
              type="button"
              class="grid size-7 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/5 hover:text-destructive"
              title="Planung löschen"
              aria-label="Planung löschen"
              [disabled]="isDeletingPlanning()"
              [attr.aria-busy]="isDeletingPlanning()"
              (click)="onDeletePlanning(planning.id)"
            >
              <ng-icon name="lucideTrash2" size="14" />
            </button>
          }
        </div>
      </div>

      @if (compact() && planning.status !== 'completed') {
        <p class="border-t border-border py-3 text-sm" [class.text-danger]="planning.status === 'failed'" [class.text-muted-foreground]="planning.status !== 'failed'">
          @switch (planning.status) {
            @case ('running') { Planung wird berechnet. Die bisherigen Planungen bleiben verfügbar. }
            @case ('failed') { {{ plannerMessage(planning.errorMessage) || 'Die Planung konnte nicht abgeschlossen werden.' }} }
            @default { Planung abgebrochen, bevor ein Ergebnis vorlag. }
          }
        </p>
      } @else if (planning.status === 'running') {
        <div class="rounded-2xl border border-dashed border-primary/20">
          <app-state-panel state="loading" title="Planung wird berechnet" description="Die bisherigen Planungen bleiben währenddessen verfügbar." />
        </div>
      } @else if (planning.status === 'failed') {
        <div class="rounded-2xl border border-dashed border-destructive/30">
          <app-state-panel state="error" title="Diese Planung ist fehlgeschlagen" [description]="plannerMessage(planning.errorMessage) || 'Die Planung konnte nicht abgeschlossen werden.'">
            @if (mode() === "admin") {
              <button
                type="button"
                class="inline-flex h-8 items-center gap-2 rounded-lg bg-primary px-3 text-xs font-normal text-primary-foreground transition-colors hover:bg-primary/90"
                (click)="onRetryPlanning(planning.id)"
              >
                <ng-icon name="lucideRotateCcw" size="14" />
                Planung erneut starten
              </button>
            }
          </app-state-panel>
        </div>
      } @else if (planning.status === 'cancelled') {
        <div class="rounded-2xl border border-dashed border-border">
          <app-state-panel state="empty" title="Planung abgebrochen" description="Diese Ausführung wurde beendet, bevor ein Ergebnis vorlag." />
        </div>
      } @else {
        @if (isPlanningStale(planning)) {
          <p class="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            <ng-icon name="lucideInfo" size="13" />
            Diese Planung basiert auf einem älteren Analysestand. Erstellen Sie eine neue Planung für aktuelle Ergebnisse.
          </p>
        }

        <div class="mt-1 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
        <div class="py-5 pr-4">
          <p class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Leistung</p>
          <p class="mt-2 text-2xl font-bold tracking-tight tabular-nums text-foreground">{{ formatNumber(planning.plantCapacity) }} <span class="text-2xs text-muted-foreground">kWp</span></p>
        </div>
        <div class="py-5 pr-4">
          <p class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Module</p>
          <p class="mt-2 text-2xl font-bold tracking-tight tabular-nums text-foreground">{{ planning.numberModels }} <span class="text-2xs text-muted-foreground">Stk.</span></p>
        </div>
        <div class="py-5 pr-4">
          <p class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">{{ planning.type.toLowerCase() === 'lease' ? 'Anlagenwert' : 'Investition inkl. Speicher' }}</p>
          <p class="mt-2 text-2xl font-bold tracking-tight tabular-nums text-foreground">{{ formatCurrency(planning.totalInvestment) }}</p>
          @if (planning.type.toLowerCase() === 'lease') {
            <p class="mt-1 text-xs text-muted-foreground">Keine Mietrate</p>
          }
        </div>
        <div class="py-5 pr-4">
          <p class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Ersparnis</p>
          <p class="mt-2 text-2xl font-bold tracking-tight tabular-nums text-foreground">{{ formatCurrency(planning.savings || 0) }}</p>
        </div>
        <div class="py-5 pr-4">
          <p class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Jährliche Rendite</p>
          <p class="mt-2 text-2xl font-bold tracking-tight tabular-nums text-foreground">{{ formatAnnualReturn(planning.annualReturn) }}</p>
        </div>
        <div class="py-5">
          <p class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Amortisation</p>
          <p class="mt-2 text-2xl font-bold tracking-tight tabular-nums text-foreground">{{ formatNumber(planning.amortizationPeriod) }} @if (planning.amortizationPeriod != null) { <span class="text-2xs text-muted-foreground">Jahre</span> }</p>
        </div>
        </div>
      }
      </section>
    }
  }
}

@if (showTemplateSection() && selectedPlanning(); as planning) {
  <section class="pt-2">
    <div class="flex items-center gap-2 border-b border-border/60 pb-3">
      <p class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Konfiguration</p>
      <span class="ml-auto font-mono text-2xs text-muted-foreground">{{ getPlanningTypeLabel(planning) }}</span>
    </div>

    <dl class="divide-y divide-border/50">
      <div class="flex items-baseline gap-3 py-2.5">
        <dt class="w-24 shrink-0 text-xs text-muted-foreground">Vorlage</dt>
        <dd class="min-w-0 flex-1 truncate text-right text-xs font-normal text-foreground">{{ planning.template?.name || "—" }}</dd>
      </div>
      <div class="flex items-baseline gap-3 py-2.5">
        <dt class="w-24 shrink-0 text-xs text-muted-foreground">Planungsart</dt>
        <dd class="min-w-0 flex-1 truncate text-right text-xs font-normal text-foreground">{{ getPlanningTypeLabel(planning) || "—" }}</dd>
      </div>
      <div class="flex items-baseline gap-3 py-2.5">
        <dt class="w-24 shrink-0 text-xs text-muted-foreground">Jahresverbrauch</dt>
        <dd class="min-w-0 flex-1 text-right font-mono text-2xs text-foreground">{{ planning.powerConsumption == null ? '—' : formatNumber(planning.powerConsumption) + ' kWh/Jahr' }}</dd>
      </div>
      <div class="flex items-baseline gap-3 py-2.5">
        <dt class="w-24 shrink-0 text-xs text-muted-foreground">Strompreis</dt>
        <dd class="min-w-0 flex-1 text-right font-mono text-2xs text-foreground">{{ formatEuroPerKwh(getPlanningElectricityPrice(planning)) }}</dd>
      </div>
      <div class="flex items-baseline gap-3 py-2.5">
        <dt class="w-24 shrink-0 text-xs text-muted-foreground">Modul</dt>
        <dd class="min-w-0 flex-1 text-right text-xs font-normal">
          @if (planning.resultSnapshot?.module || planning.module; as equipment) {
            <span class="inline-flex max-w-full items-center gap-1.5">
              <span class="truncate" [title]="equipment.name || ''">{{ equipment.name || 'Name nicht verfügbar' }}</span>
              @if (planning.module?.id) { <a class="inline-flex shrink-0 text-muted-foreground hover:text-primary" [routerLink]="[catalogBase(), 'modules']" [queryParams]="{ equipment: planning.module?.id }" aria-label="Modul im Katalog öffnen" title="Im Katalog öffnen"><ng-icon name="lucideExternalLink" size="13" /></a> }
            </span>

          } @else { {{ '—' }} }
        </dd>
      </div>
      <div class="flex items-baseline gap-3 py-2.5">
        <dt class="w-24 shrink-0 text-xs text-muted-foreground">Wechselrichter</dt>
        <dd class="min-w-0 flex-1 text-right text-xs font-normal">
          @if (planning.resultSnapshot?.inverter || planning.inverter; as equipment) {
            <span class="inline-flex max-w-full items-center gap-1.5">
              <span class="truncate" [title]="equipment.name || ''">{{ equipment.name || 'Name nicht verfügbar' }}</span>
              @if (planning.inverter?.id) { <a class="inline-flex shrink-0 text-muted-foreground hover:text-primary" [routerLink]="[catalogBase(), 'inverters']" [queryParams]="{ equipment: planning.inverter?.id }" aria-label="Wechselrichter im Katalog öffnen" title="Im Katalog öffnen"><ng-icon name="lucideExternalLink" size="13" /></a> }
            </span>

          } @else { {{ '—' }} }
        </dd>
      </div>
      <div class="flex items-baseline gap-3 py-2.5">
        <dt class="w-24 shrink-0 text-xs text-muted-foreground">Speicher</dt>
        <dd class="min-w-0 flex-1 text-right text-xs font-normal">
          @if (planning.resultSnapshot?.storage || planning.storage; as equipment) {
            <span class="inline-flex max-w-full items-center gap-1.5">
              <span class="truncate" [title]="equipment.name || ''">{{ equipment.name || 'Name nicht verfügbar' }}</span>
              @if (planning.storage?.id) { <a class="inline-flex shrink-0 text-muted-foreground hover:text-primary" [routerLink]="[catalogBase(), 'storages']" [queryParams]="{ equipment: planning.storage?.id }" aria-label="Speicher im Katalog öffnen" title="Im Katalog öffnen"><ng-icon name="lucideExternalLink" size="13" /></a> }
            </span>
            @if (equipment.size != null) { <span class="block text-muted-foreground">{{ equipment.size }} kWh</span> }
          } @else { {{ planning.resultSnapshot ? 'Ohne Speicher' : '—' }} }
        </dd>
      </div>
      <div class="flex items-baseline gap-3 py-2.5">
        <dt class="min-w-24 text-xs text-muted-foreground">Investition in Speicher</dt>
        <dd class="ml-auto text-right font-mono text-2xs text-foreground">{{ planning.storageCosts == null ? '—' : formatCurrency(planning.storageCosts) }}</dd>
      </div>
      <div class="flex items-baseline gap-3 py-2.5">
        <dt class="w-24 shrink-0 text-xs text-muted-foreground">CO₂-Ersparnis</dt>
        <dd class="min-w-0 flex-1 text-right font-mono text-2xs text-foreground">{{ formatNumber(planning.co2Savings) }} kg/Jahr</dd>
      </div>
      <div class="flex items-baseline gap-3 py-2.5">
        <dt class="w-24 shrink-0 text-xs text-muted-foreground">Stromkosten</dt>
        <dd class="min-w-0 flex-1 text-right font-mono text-2xs text-foreground">{{ formatEuroPerKwh(planning.electricityGenerationCosts || 0) }}</dd>
      </div>
    </dl>
  </section>
}

@if (showChartsSection() && selectedPlanning(); as planning) {
  @if (planning.status === 'completed') {
    <section class="mb-8 space-y-4" aria-label="Kostenübersicht">
      <div class="flex flex-wrap items-baseline justify-between gap-2 border-b border-border pb-3">
        <h3 class="text-sm font-bold">{{ planning.type.toLowerCase() === 'lease' ? 'Anlagenwert nach Posten' : 'Investition nach Posten' }}</h3>
        <p class="text-xs text-muted-foreground">Gesamt <strong class="ml-2 text-lg font-bold tabular-nums text-foreground">{{ formatCurrency(planning.totalInvestment) }}</strong></p>
      </div>
      <div class="flex flex-wrap items-center gap-6 py-2">
        <div class="relative mx-auto size-56 shrink-0">
          <svg viewBox="0 0 120 120" class="size-full -rotate-90" aria-label="Kostenaufteilung">
            <circle cx="60" cy="60" r="48" fill="none" stroke="var(--muted)" stroke-width="14" />
            @for (row of investmentChart(); track row.key) {
              @if (row.arc > 0) {
                <circle cx="60" cy="60" r="48" fill="none" [attr.stroke]="row.color" stroke-width="14" pathLength="100"
                  [attr.stroke-dasharray]="row.arc + ' ' + (100 - row.arc)" [attr.stroke-dashoffset]="-row.offset"
                  class="transition-opacity duration-200" [attr.opacity]="hoveredCost() && hoveredCost() !== row.key ? 0.25 : 1"
                  (mouseenter)="hoveredCost.set(row.key)" (mouseleave)="hoveredCost.set(null)">
                  <title>{{ row.label }}: {{ formatCurrency(row.amount) }}</title>
                </circle>
              }
            }
          </svg>
          <div class="pointer-events-none absolute inset-8 flex flex-col items-center justify-center text-center">
            <span class="text-2xs uppercase tracking-wider text-muted-foreground">{{ highlightedCost()?.label || 'Gesamtinvestition' }}</span>
            <strong class="mt-2 text-lg font-bold tabular-nums">{{ formatCurrency(highlightedCost() ? highlightedCost()!.amount : planning.totalInvestment) }}</strong>
            @if (highlightedCost()?.share != null) { <span class="mt-1 text-xs text-muted-foreground">{{ formatNumber(highlightedCost()!.share) }} %</span> }
          </div>
        </div>
        <div class="min-w-[220px] flex-1 space-y-1">
          @for (row of investmentChart(); track row.key) {
            <button type="button" class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-xs transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-ring"
              (mouseenter)="hoveredCost.set(row.key)" (mouseleave)="hoveredCost.set(null)"
              (focus)="hoveredCost.set(row.key)" (blur)="hoveredCost.set(null)" (click)="hoveredCost.set(hoveredCost() === row.key ? null : row.key)">
              <span class="size-2.5 shrink-0 rounded-full" [style.background]="row.color"></span>
              <span class="flex-1">{{ row.label }}</span>
              <span class="text-muted-foreground tabular-nums">{{ row.share == null ? '' : formatNumber(row.share) + ' %' }}</span>
              <strong class="min-w-20 text-right font-normal tabular-nums">{{ row.amount == null ? '—' : formatCurrency(row.amount) }}</strong>
            </button>
          }
        </div>
      </div>
      <div class="flex items-baseline justify-between border-t border-border pt-3 text-xs text-muted-foreground"><span>Anlagenwert je kWp · inkl. Speicher</span><span class="font-normal tabular-nums">{{ formatCurrency(investmentPerKwp(planning)) }} / kWp</span></div>
      @if (planning.resultSnapshot; as snapshot) {
        <p class="text-xs leading-relaxed text-muted-foreground">Betriebskosten über {{ snapshot.calculation_period_years }} Jahre: {{ formatCurrency(planning.operatingCostsTotal) }}</p>
      }
      @if (planning.type.toLowerCase() === 'lease') { <p class="text-xs text-muted-foreground">Der Anlagenwert ist keine Einmalzahlung. Für Sie gelten die monatlichen Zahlungen der Mietübersicht.</p> }
    </section>
  }
}

@if (showChartsSection() && hasPlannings()) {
  <section class="min-w-0 space-y-10">
    @if (selectedPlanning()?.status === 'running') {
      <div class="rounded-2xl border border-dashed border-primary/20">
        <app-state-panel state="loading" title="Ergebnisse werden berechnet" />
      </div>
    } @else if (selectedPlanning()?.status === 'failed') {
      <div class="rounded-2xl border border-dashed border-destructive/30">
        <app-state-panel state="error" title="Keine Ergebnisse für diese Planung" [description]="plannerMessage(selectedPlanning()?.errorMessage)" />
      </div>
    } @else if (selectedPlanning()?.status === 'cancelled') {
      <div class="rounded-2xl border border-dashed border-border">
        <app-state-panel state="empty" title="Planung abgebrochen" />
      </div>
    } @else if (isLoadingChart()) {
      <div class="rounded-2xl border border-dashed border-border">
        <app-state-panel state="loading" title="Diagrammdaten werden geladen" />
      </div>
    } @else if (chartError()) {
      <div class="rounded-2xl border border-dashed border-destructive/30">
        <app-state-panel state="error" title="Diagrammdaten konnten nicht geladen werden" [description]="chartError()" />
      </div>
    } @else if (!hasChartsAvailable()) {
      <div class="rounded-2xl border border-dashed border-border">
        <app-state-panel state="empty" title="Keine Diagrammdaten verfügbar" />
      </div>
    } @else if (isVisible()) {
      @if (costChartOptions()) {
        <div>
          <div class="flex items-center gap-3 border-b border-border pb-2.5">
            <h3 class="text-sm font-bold text-foreground">Kosten über 20 Jahre</h3>
          </div>
          <apx-chart class="mt-3 block"
            [series]="costChartOptions()!.series"
            [chart]="costChartOptions()!.chart"
            [xaxis]="costChartOptions()!.xaxis"
            [yaxis]="costChartOptions()!.yaxis"
            [stroke]="costChartOptions()!.stroke"
            [fill]="costChartOptions()!.fill"
            [tooltip]="costChartOptions()!.tooltip"
            [legend]="costChartOptions()!.legend"
            [dataLabels]="costChartOptions()!.dataLabels"
            [grid]="costChartOptions()!.grid"
            [colors]="costChartOptions()!.colors"
          />
        </div>
      }

      <div class="min-w-0">
        <div class="flex items-center gap-6 border-b border-border" role="tablist" aria-label="Energiediagramm">
          <button
            type="button"
            role="tab"
            class="-mb-px border-b-2 border-transparent px-0 pb-2.5 text-sm font-normal transition-colors"
            [class.border-primary]="activeEnergyChart() === 'monthly'"
            [class.text-foreground]="activeEnergyChart() === 'monthly'"
            [class.text-muted-foreground]="activeEnergyChart() !== 'monthly'"
            [attr.aria-selected]="activeEnergyChart() === 'monthly'"
            (click)="activeEnergyChart.set('monthly')"
          >Monatsbilanz</button>
          <button
            type="button"
            role="tab"
            class="-mb-px border-b-2 border-transparent px-0 pb-2.5 text-sm font-normal transition-colors"
            [class.border-primary]="activeEnergyChart() === 'daily'"
            [class.text-foreground]="activeEnergyChart() === 'daily'"
            [class.text-muted-foreground]="activeEnergyChart() !== 'daily'"
            [attr.aria-selected]="activeEnergyChart() === 'daily'"
            (click)="activeEnergyChart.set('daily')"
          >Tagesprofil</button>
        </div>

        @if (activeEnergyChart() === 'monthly' && monthlyChartOptions()) {
            <apx-chart class="mt-3 block"
              [series]="monthlyChartOptions()!.series"
              [chart]="monthlyChartOptions()!.chart"
              [xaxis]="monthlyChartOptions()!.xaxis"
              [yaxis]="monthlyChartOptions()!.yaxis"
              [stroke]="monthlyChartOptions()!.stroke"
              [fill]="monthlyChartOptions()!.fill"
              [tooltip]="monthlyChartOptions()!.tooltip"
              [legend]="monthlyChartOptions()!.legend"
              [dataLabels]="monthlyChartOptions()!.dataLabels"
              [plotOptions]="monthlyChartOptions()!.plotOptions"
              [grid]="monthlyChartOptions()!.grid"
              [colors]="monthlyChartOptions()!.colors"
            />
        }

        @if (activeEnergyChart() === 'daily' && dailyChartOptions()) {
            <apx-chart class="mt-3 block"
              [series]="dailyChartOptions()!.series"
              [chart]="dailyChartOptions()!.chart"
              [xaxis]="dailyChartOptions()!.xaxis"
              [yaxis]="dailyChartOptions()!.yaxis"
              [stroke]="dailyChartOptions()!.stroke"
              [fill]="dailyChartOptions()!.fill"
              [tooltip]="dailyChartOptions()!.tooltip"
              [legend]="dailyChartOptions()!.legend"
              [dataLabels]="dailyChartOptions()!.dataLabels"
              [grid]="dailyChartOptions()!.grid"
              [colors]="dailyChartOptions()!.colors"
            />
        }
      </div>
    }
  </section>
}
